import React, { useMemo } from 'react';
import { Eye } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import { prepareSignatureHtmlForEditor, preserveSignatureSpaces } from '../emails/utils/signatureUtils';
import { sanitizeRichEmailHtml } from '../emails/utils/sanitizeEmailBody';

interface SignaturePreviewProps {
  html: string;
  className?: string;
  emptyMessage?: string;
}

function signaturePreviewHasContent(html: string): boolean {
  if (!html.trim()) return false;
  if (typeof document === 'undefined') return true;
  const root = document.createElement('div');
  root.innerHTML = html;
  if (root.querySelector('img[src]')) return true;
  return (root.textContent ?? '').replace(/\u00a0/g, ' ').trim().length > 0;
}

const SignaturePreview: React.FC<SignaturePreviewProps> = ({
  html,
  className,
  emptyMessage = 'Start typing to see how your signature will look.',
}) => {
  const { colors } = useTheme();

  const prepared = useMemo(
    () =>
      sanitizeRichEmailHtml(preserveSignatureSpaces(prepareSignatureHtmlForEditor(html || ''))),
    [html],
  );

  const hasContent = useMemo(() => signaturePreviewHasContent(prepared), [prepared]);

  return (
    <div className={cn('space-y-2', className)}>
      <div className="flex items-center gap-2">
        <Eye className={cn('h-4 w-4', colors.textMuted)} aria-hidden />
        <p className={cn('text-sm font-medium', colors.text)}>Live preview</p>
      </div>
      <p className={cn('text-xs', colors.textMuted)}>
        How recipients will see your signature below a message.
      </p>

      <div className={cn('overflow-hidden rounded-xl border shadow-sm', colors.border)}>
        <div
          className={cn(
            'flex items-center gap-2 border-b px-4 py-2.5',
            colors.border,
            colors.surfaceHover,
          )}
        >
          <span className="h-2.5 w-2.5 rounded-full bg-red-400/80" aria-hidden />
          <span className="h-2.5 w-2.5 rounded-full bg-amber-400/80" aria-hidden />
          <span className="h-2.5 w-2.5 rounded-full bg-emerald-400/80" aria-hidden />
          <span className={cn('ml-2 text-[11px] font-medium uppercase tracking-wider', colors.textMuted)}>
            Compose preview
          </span>
        </div>

        <div className="bg-white p-4 dark:bg-slate-950 sm:p-5">
          <div className={cn('space-y-1 border-b pb-4 text-xs', colors.border)}>
            <div className="flex gap-2">
              <span className="w-12 shrink-0 font-medium text-slate-500 dark:text-slate-400">From</span>
              <span className="text-slate-700 dark:text-slate-300">you@company.com</span>
            </div>
            <div className="flex gap-2">
              <span className="w-12 shrink-0 font-medium text-slate-500 dark:text-slate-400">Subject</span>
              <span className="text-slate-700 dark:text-slate-300">Sample message</span>
            </div>
          </div>

          <div className="py-4 text-sm leading-relaxed text-slate-700 dark:text-slate-300">
            <p>Hi there,</p>
            <p className="mt-2">Thanks for your message. Here is a quick reply before my signature.</p>
          </div>

          <div className={cn('border-t border-dashed pt-4', colors.border)}>
            <p className={cn('mb-3 text-[10px] font-semibold uppercase tracking-wider', colors.textMuted)}>
              Signature
            </p>
            {hasContent ? (
              <div
                className={cn(
                  'text-sm leading-relaxed text-slate-800 dark:text-slate-200',
                  '[&_p]:whitespace-pre-wrap [&_div]:whitespace-pre-wrap',
                  '[&_h1]:whitespace-pre-wrap [&_h2]:whitespace-pre-wrap [&_h3]:whitespace-pre-wrap',
                  '[&_a]:text-indigo-600 [&_a]:underline dark:[&_a]:text-indigo-400',
                  '[&_img]:max-w-full [&_img]:h-auto [&_img]:block',
                  '[&_table]:border-collapse [&_td]:border [&_td]:border-slate-200 [&_td]:p-1 [&_td]:whitespace-pre-wrap',
                )}
                dangerouslySetInnerHTML={{ __html: prepared }}
              />
            ) : (
              <p className={cn('text-sm italic', colors.textMuted)}>{emptyMessage}</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignaturePreview;
